/**
 * Cognito SRP authentication helpers using amazon-cognito-identity-js.
 *
 * All functions are no-ops or throw when VITE_COGNITO_ENABLED !== 'true'.
 * The frontend calls these directly for SRP + MFA auth, then sends the
 * resulting tokens to the backend POST /api/auth/cognito-session for
 * HttpOnly cookie session establishment.
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// ─── Configuration ───────────────────────────────────────────────────────────

const COGNITO_ENABLED = import.meta.env.VITE_COGNITO_ENABLED === 'true';
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

let userPool: CognitoUserPool | null = null;

if (COGNITO_ENABLED && USER_POOL_ID && CLIENT_ID) {
  userPool = new CognitoUserPool({
    UserPoolId: USER_POOL_ID,
    ClientId: CLIENT_ID,
  });
}

export function isCognitoEnabled(): boolean {
  return COGNITO_ENABLED;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CognitoAuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export type CognitoSignInResult =
  | { type: 'SUCCESS'; tokens: CognitoAuthTokens }
  | { type: 'NEW_PASSWORD_REQUIRED'; userAttributes: Record<string, string>; cognitoUser: CognitoUser }
  | { type: 'MFA_REQUIRED'; challengeName: string; cognitoUser: CognitoUser }
  | { type: 'MFA_SETUP'; cognitoUser: CognitoUser };

// ─── Sign In (SRP) ──────────────────────────────────────────────────────────

/**
 * Authenticate with email/password via SRP protocol.
 * Returns either tokens (success), or a challenge that needs further interaction.
 */
export function cognitoSignIn(email: string, password: string): Promise<CognitoSignInResult> {
  if (!userPool) return Promise.reject(new Error('Cognito is not configured'));

  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess(session: CognitoUserSession) {
        resolve({
          type: 'SUCCESS',
          tokens: extractTokens(session),
        });
      },
      onFailure(err: Error) {
        reject(mapCognitoError(err));
      },
      newPasswordRequired(userAttributes: Record<string, string>) {
        resolve({ type: 'NEW_PASSWORD_REQUIRED', userAttributes, cognitoUser });
      },
      totpRequired() {
        resolve({ type: 'MFA_REQUIRED', challengeName: 'SOFTWARE_TOKEN_MFA', cognitoUser });
      },
      mfaSetup() {
        resolve({ type: 'MFA_SETUP', cognitoUser });
      },
    });
  });
}

// ─── MFA: Respond to TOTP challenge ─────────────────────────────────────────

/**
 * Submit the 6-digit TOTP code to complete MFA verification.
 */
export function cognitoRespondToMfaChallenge(
  cognitoUser: CognitoUser,
  totpCode: string,
): Promise<CognitoAuthTokens> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      totpCode,
      {
        onSuccess(session: CognitoUserSession) {
          resolve(extractTokens(session));
        },
        onFailure(err: Error) {
          reject(mapCognitoError(err));
        },
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

// ─── MFA: Setup TOTP ─────────────────────────────────────────────────────────

/**
 * Get the TOTP secret for QR code generation during MFA setup.
 * Returns the secret code that should be encoded into an otpauth:// URI.
 */
export function cognitoSetupTotp(cognitoUser: CognitoUser): Promise<string> {
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode(secretCode: string) {
        resolve(secretCode);
      },
      onFailure(err: Error) {
        reject(mapCognitoError(err));
      },
    });
  });
}

/**
 * Verify the TOTP setup by confirming the first code from the authenticator app.
 */
export function cognitoVerifyTotp(
  cognitoUser: CognitoUser,
  totpCode: string,
): Promise<CognitoAuthTokens | null> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(totpCode, 'TOTP Device', {
      onSuccess(session: CognitoUserSession) {
        resolve(extractTokens(session));
      },
      onFailure(err: Error) {
        reject(mapCognitoError(err));
      },
    });
  });
}

// ─── New Password (first login) ─────────────────────────────────────────────

/**
 * Complete the NEW_PASSWORD_REQUIRED challenge (first login with temp password).
 */
export function cognitoCompleteNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
): Promise<CognitoSignInResult> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess(session: CognitoUserSession) {
        resolve({ type: 'SUCCESS', tokens: extractTokens(session) });
      },
      onFailure(err: Error) {
        reject(mapCognitoError(err));
      },
      mfaSetup() {
        resolve({ type: 'MFA_SETUP', cognitoUser });
      },
      totpRequired() {
        resolve({ type: 'MFA_REQUIRED', challengeName: 'SOFTWARE_TOKEN_MFA', cognitoUser });
      },
    });
  });
}

// ─── Forgot / Reset Password ─────────────────────────────────────────────────

/** Confirm password reset with verification code + new password. */
export function cognitoConfirmPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!userPool) return Promise.reject(new Error('Cognito is not configured'));

  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess() {
        resolve();
      },
      onFailure(err: Error) {
        reject(mapCognitoError(err));
      },
    });
  });
}

// ─── Sign Out ────────────────────────────────────────────────────────────────

/** Global sign out — revokes all tokens. */
export function cognitoGlobalSignOut(): Promise<void> {
  if (!userPool) return Promise.resolve();

  const currentUser = userPool.getCurrentUser();
  if (!currentUser) return Promise.resolve();

  return new Promise((resolve) => {
    currentUser.globalSignOut({
      onSuccess() {
        resolve();
      },
      onFailure() {
        // ponytail: Best-effort — backend cookie clear handles the rest
        resolve();
      },
    });
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractTokens(session: CognitoUserSession): CognitoAuthTokens {
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
}

/** Map Cognito SDK errors to user-friendly messages. */
function mapCognitoError(err: Error & { code?: string }): Error {
  const code = err.code || err.name || '';
  const mapped = COGNITO_ERROR_MAP[code];
  if (mapped) {
    const error = new Error(mapped) as Error & { code: string };
    error.code = code;
    return error;
  }
  return err;
}

const COGNITO_ERROR_MAP: Record<string, string> = {
  NotAuthorizedException: 'Invalid email or password',
  UserNotFoundException: 'Invalid email or password',
  UserNotConfirmedException: 'Account not confirmed. Check your email.',
  CodeMismatchException: 'Invalid verification code. Please try again.',
  ExpiredCodeException: 'Verification code has expired. Please request a new one.',
  InvalidPasswordException: 'Password does not meet requirements (12+ chars, upper, lower, number, symbol).',
  LimitExceededException: 'Too many attempts. Please try again later.',
  TooManyRequestsException: 'Too many requests. Please try again later.',
  PasswordResetRequiredException: 'Password reset required. Please use forgot password.',
  InvalidParameterException: 'Invalid input. Please check your entries.',
  EnableSoftwareTokenMFAException: 'MFA verification failed. Please try again.',
};
